import React, { useEffect, useState, useContext, FC } from 'react';
import styled from 'styled-components';

import { AriaAlertContext } from '../../AriaAlertContext';
import CheckGray from '../../assets/icons/checkmark-gray.svg';
import CheckWhite from '../../assets/icons/checkmark-white.svg';
import { colors } from '../constants';
import { getFieldClone } from '../utilities/syntheticEventTracking';

import {
  ErrorMessageBubble,
  ErrorMessageBubbleIcon,
  ErrorMessageText,
} from './MessageBubble';
import { StyledInputProps } from './TextInput';

// CheckBox
// Provides basic functionalities for checkbox inputs
// How it works:
//   - There are two main elements, one "fake" div and one "real" input
//   - The "fake" div is visible, and handles user interactions with the appearance of a checkbox
//   - The "real" input is obscured, and syncs its value with isChecked state of fake checkbox
// Motivation for using the fake/real system:
//   - Less custom functionality: can use built-in behavior of input with type=checkbox
//   - More custom styling: easier to style div elements than type=checkbox input elements

// Interfaces -------------------------------------------------------------------------------------

interface CheckBoxInputProps {
  name: string;
  value: boolean;
  children: React.ReactNode;
  required?: boolean;
  setFieldValue: (name: string, value: boolean) => void;
  setFieldTouched: Function;
  disabled?: boolean;
  error?: any;
  touched?: any;
  onFocus?: Function;
  onChange:
    | (((e: React.ChangeEvent<any>) => void) & ((text: string) => void))
    | (() => undefined);
  ariaLabelledBy?: string;
}

// Styles -----------------------------------------------------------------------------------------

// Spans width of parent, contains checkbox and adjacent text content
const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
`;
Container.displayName = 'Container';

// Handles user interactions as the styled, "fake" checkbox shown on the page
const Input = styled.div<StyledInputProps>`
  position: relative;
  height: 1.5rem;
  width: 1.5rem;
  appearance: none;
  border: 0.0625rem solid ${colors.UI_PRIMARY};
  border-radius: 0.125rem;
  outline: none;
  cursor: pointer;
  box-shadow: none;
  background-color: transparent;

  ${(p: StyledInputProps) =>
    p.showError &&
    `
    box-shadow: 0 0 0 0.0625rem ${colors.TEXT_ERROR};
    border-color: ${colors.TEXT_ERROR};
  `};
  ${(p: StyledInputProps) =>
    p.isChecked &&
    `
    background-color: ${colors.UI_PRIMARY};
  `};

  /* Hover/Focus - changes border and background color to accent color and adds box shadow */
  /* When an error exists, removes background and applies red color to border and box-shadow */
  &:hover,
  &:focus {
    box-shadow: 0 0 0 0.0625rem ${colors.UI_PRIMARY_ACCENT};
    border-color: ${colors.UI_PRIMARY_ACCENT};
    background-color: ${colors.UI_PRIMARY_ACCENT}26;

    ${(p: StyledInputProps) =>
      p.isChecked &&
      `
      background-color: ${colors.UI_PRIMARY_ACCENT};
    `};
    ${(p: StyledInputProps) =>
      p.isDisabled &&
      `
      box-shadow: none;
      border-color: ${colors.BORDER_PRIMARY};
      background-color: #EFEFEF4D;
      cursor: no-drop;
    `};
  }

  /* undo on-focus UI if focus-visible property is supported */
  &:focus:not(:hover, :focus-visible) {
    box-shadow: none;
    border-color: ${colors.UI_PRIMARY};
    background-color: transparent;

    ${(p: StyledInputProps) =>
      p.isChecked &&
      `
      background-color: ${colors.UI_PRIMARY};
    `};
    ${(p: StyledInputProps) =>
      p.showError &&
      `
      box-shadow: 0 0 0 0.0625rem ${colors.TEXT_ERROR};
      border-color: ${colors.TEXT_ERROR};
    `};
  }

  /* apply on-focus UI only when keyboard-focused */
  &:focus-visible {
    box-shadow: 0 0 0 0.0625rem ${colors.UI_PRIMARY_ACCENT};
    border-color: ${colors.UI_PRIMARY_ACCENT};
    background-color: ${colors.UI_PRIMARY_ACCENT}26;

    ${(p: StyledInputProps) =>
      p.isChecked &&
      `
      background-color: ${colors.UI_PRIMARY_ACCENT};
    `};
    ${(p: StyledInputProps) =>
      p.isDisabled &&
      `
      box-shadow: none;
      border-color: ${colors.BORDER_PRIMARY};
      background-color: #EFEFEF4D;
      cursor: no-drop;
    `};
  }
  ${(p: StyledInputProps) =>
    p.isDisabled &&
    `
    box-shadow: none;
    border-color: ${colors.BORDER_PRIMARY};
    background-color: #EFEFEF4D;
    cursor: no-drop;
  `};
`;
Input.displayName = 'Input';

const InputCheckWrapper = styled.div<StyledInputProps>`
  height: 100%;
  width: 100%;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const InputCheckImage = ({ isChecked, isDisabled }: StyledInputProps) => {
  if (!isChecked) return null;

  if (isDisabled) {
    return (
      <InputCheckWrapper>
        <CheckGray data-testid="check-icon-gray" />
      </InputCheckWrapper>
    );
  }

  return (
    <InputCheckWrapper>
      <CheckWhite data-testid="check-icon-white" />
    </InputCheckWrapper>
  );
};

const InputContainer = styled.div<StyledInputProps>`
  margin: 0.125rem 0.875rem 0 0;
  height: 1.5rem;
  width: 1.5rem;
  color: ${colors.TEXT_LABEL};
  ${(p: StyledInputProps) =>
    p.showError &&
    `
    color: ${colors.TEXT_ERROR};
  `};
`;
InputContainer.displayName = 'InputContainer';

// Contains text content to display next to checkbox
const TextContainer = styled.div``;
TextContainer.displayName = 'TextContainer';

// Contains the checkbox and copy to the left of it
const Label = styled.label`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
`;
Label.displayName = 'Label';

// The actual input is obscured and contained within the fake checkbox
const RealInput = styled.input<StyledInputProps>`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  height: 100%;
  width: 100%;
  margin: 0;
  display: none;
  appearance: none;
  outline: none;
  pointer-events: none;
`;
RealInput.displayName = 'RealInput';

// Hidden error message is revealed when an error exists for a touched input
// Props:
// visible - toggles display between flex and none
const ErrorMessage = styled(ErrorMessageBubble)<StyledInputProps>`
  margin: 0.5625rem 0 0;
  display: none;
  ${(p: StyledInputProps) =>
    p.isVisible &&
    `
    display: flex;
  `};

  &::before {
    left: 0.5625rem;
  }
`;
ErrorMessage.displayName = 'ErrorMessage';

// Component --------------------------------------------------------------------------------------

export const CheckBox: FC<CheckBoxInputProps> = ({
  name,
  value,
  children,
  setFieldValue,
  setFieldTouched,
  onChange,
  required = true,
  disabled = false,
  error = undefined,
  touched = undefined,
  onFocus = () => {},
  ariaLabelledBy = undefined,
}) => {
  // State ----------------------------------------------------------------------------------------

  // Visual indication of isChecked status
  const [isChecked, setIsChecked] = useState(value);

  // Show error message
  const [showValidation, setShowValidation] = useState(touched);

  // Effects --------------------------------------------------------------------------------------

  const { setAriaAlertMessage } = useContext(AriaAlertContext);

  // Functions ------------------------------------------------------------------------------------

  // onBlur - changes state to indicate input is touched and calls blur handler from props
  const handleBlur = () => {
    // Need to setFieldTouched rather than the usual onBlur since this is the fake input blur event
    if (setFieldTouched) setFieldTouched();
    if (error && setAriaAlertMessage) {
      setAriaAlertMessage(error);
    }
  };

  // Maintain accessibility by toggling isChecked when user presses space in focused checkbox
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    // Restrict visual changes when real input is disabled
    // Accept both enter and space keys for checking/unchecking checkbox
    if (e !== undefined && e.code === 'Enter' && !disabled) {
      if (setFieldValue) setFieldValue(name, !isChecked);
    }
    if (e !== undefined && e.code === 'Space' && !disabled) {
      if (setFieldValue) setFieldValue(name, !isChecked);
    }
  };

  // Catch-all workaround to inconsistent browser event tracking
  const trackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      // Find the form containing the real checkbox input awaiting its change event
      const { id, form, checked } = e.target;
      // Create a SyntheticEvent of type 'change' to leverage default Heap analytics tracking
      const syntheticEvent = new Event('change', e.nativeEvent);
      let clone = getFieldClone(id);
      // Create a clone of the real checkbox input if one is not already appended to the document
      if (!clone) clone = cloneField(id, document.getElementById(id), form);
      // CheckBox component implementation requires the input value of clone to be set directly
      clone.setAttribute('value', checked.toString());
      // Dispatch the synthetic event to simulate the change
      clone.dispatchEvent(syntheticEvent);
    } catch {
      // TO-DO error handler - probably not worth a NPC-Client-Error
    }
  };

  // Update display state of checkbox using the real input value
  useEffect(() => {
    setIsChecked(value);
    if (required) setShowValidation(!value && required);
  }, [value]);

  // Show validation for touched inputs
  useEffect(() => {
    setShowValidation(touched);
  }, [touched]);

  // Render ---------------------------------------------------------------------------------------

  return (
    <>
      <Container>
        <Label htmlFor={`real-checkbox-for-${name}`} id={`${name}-copy`}>
          <InputContainer showError={error && showValidation}>
            <Input
              id={name}
              tabIndex={0} // allow user to focus using tab
              isChecked={isChecked}
              role="checkbox"
              aria-checked={isChecked}
              aria-labelledby={ariaLabelledBy}
              aria-required={required}
              aria-describedby={`${name}-error`}
              aria-invalid={error && showValidation}
              onClick={() => (disabled ? {} : setIsChecked(!isChecked))}
              onFocus={onFocus}
              onBlur={handleBlur}
              onKeyPress={handleKeyPress}
              onChange={onChange}
              showError={error && showValidation}
              isDisabled={disabled}
            >
              <InputCheckImage isChecked={isChecked} isDisabled={disabled} />
              <RealInput
                id={`real-checkbox-for-${name}`} // id is required
                name={name}
                value={value.toString()}
                checked={value}
                type="checkbox"
                disabled={disabled}
                onChange={(e) => {
                  trackChange(e);
                  onChange(e);
                }}
                data-testid={name}
                tabIndex={-1}
              />
            </Input>
          </InputContainer>
          <TextContainer>{children}</TextContainer>
        </Label>
      </Container>
      <ErrorMessage isVisible={error && showValidation}>
        <ErrorMessageText id={`${name}-error`}>{error}</ErrorMessageText>
        <ErrorMessageBubbleIcon />
      </ErrorMessage>
    </>
  );
};
